import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { Upload, RefreshCw, Sparkles, ArrowLeft, Info, Heart, MessageCircle, Download, Share2, X, Clock, Instagram, Search, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { api, FILE_URL, formatBRL, displayBrand } from "../lib/api";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import CatalogFilterPanel, { applyCatalogFilters, EMPTY_FILTERS } from "../components/CatalogFilterPanel";
import ConsultoraModal from "../components/ConsultoraModal";
import { diversifyProducts } from "../lib/diversify";
import { toast } from "sonner";

const LOADING_MESSAGES = [
  "Analisando seu estilo...",
  "Nossa consultora está avaliando a combinação...",
  "Preparando sua análise personalizada...",
  "Sua foto não será salva e é usada apenas para esta visualização. Ela será apagada assim que você finalizar a simulação.",
];

const compatLabel = (n) => {
  if (n == null) return "Muito alta";
  if (n >= 85) return "Muito alta";
  if (n >= 70) return "Alta";
  if (n >= 55) return "Boa";
  if (n >= 40) return "Razoável";
  return "Discreta";
};

// M222 — Mobile detection (used to hide the Instagram Stories button on
// desktop where the share-sheet / IG deep link don't exist). UA sniffing is
// the most reliable signal for "does this device have IG installed"; matchMedia
// alone would lie on resized desktops.
const IS_MOBILE = typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");

/**
 * Best-effort native share with the generated try-on image attached.
 * On iOS Safari and Android Chrome the OS share sheet pops up with WhatsApp,
 * Instagram, Photos, Telegram, etc as first-class targets — the image is
 * already encoded as a real file so the destination app receives it as a
 * photo, not as a link preview.
 *
 * Returns `true` when the platform accepted the share, `false` when we should
 * fall back to a URL-based share (used by the WhatsApp button on desktop).
 */
async function nativeShareWithFile({ imageUrl, filename, text }) {
  if (typeof navigator === "undefined" || !navigator.share) return false;
  try {
    const r = await fetch(imageUrl);
    if (!r.ok) return false;
    const blob = await r.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
    // navigator.canShare may not exist on older Safari; treat its absence as
    // "let's try anyway" — if the OS refuses we just catch and return false.
    if (navigator.canShare && !navigator.canShare({ files: [file] })) return false;
    await navigator.share({ files: [file], text });
    return true;
  } catch {
    return false;
  }
}


export default function TryOnExperimentPage({ productId: propProductId }) {
  const [searchParams] = useSearchParams();
  const productId = propProductId || searchParams.get("produto");
  const nav = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(productId ? "photo" : "choose-product");
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  // Shared "Falar com uma consultora" modal — opened from the Outros short-
  // circuit screen, from the small post-result CTA below share buttons, and
  // also from the new fallback CTA on the invalid_photo screen.
  const [consultoraOpen, setConsultoraOpen] = useState(false);
  // Pre-generation "wait experience" modal — shown right after the user
  // clicks "Analisar agora" so they can choose between waiting on the current
  // tab or continuing to explore the catalog in a new tab while we keep
  // polling in the background.
  const [showWaitModal, setShowWaitModal] = useState(false);
  // AudioContext must be created/resumed inside a user gesture (click handler)
  // to bypass browser autoplay restrictions; we then reuse the same context
  // to play the completion chime several minutes later when polling resolves.
  const audioCtxRef = useRef(null);
  // Remember the original document.title so we can restore it after we briefly
  // hijack it ("✨ Sua simulação está pronta!") to alert the user when their
  // result is ready while they're browsing in another tab.
  const originalTitleRef = useRef(null);

  // Redirect to login if not authenticated — capture current path so user comes back here.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const target = location.pathname + (location.search || "");
      nav(`/login?redirect=${encodeURIComponent(target)}`, { replace: true });
    }
  }, [user, authLoading, location.pathname, location.search, nav]);

  // Fetch quota once we know the user is logged in.
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      setQuotaLoading(true);
      let data;
      try {
        const r = await api.get("/tryon/quota");
        data = r.data;
      } catch {
        data = { allowed: true }; // fail open on transient error
      }
      if (!cancelled) {
        setQuota(data);
        setQuotaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Scroll to top whenever the Try-On step changes (single-route multi-step flow).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  // Picker step state — search + filters mirror the main catalogue UX so the
  // shopper can narrow the try-on list with the exact same controls.
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerFilters, setPickerFilters] = useState({ ...EMPTY_FILTERS });
  const [pickerMobileFiltersOpen, setPickerMobileFiltersOpen] = useState(false);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoPath, setPhotoPath] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [complement, setComplement] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [resultError, setResultError] = useState(null);
  const [invalidReason, setInvalidReason] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [error, setError] = useState("");
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  // Progress bar driver: ticks every 500 ms while step === "loading".
  // Estimated total = 90s (typical gpt-image-2 high-quality edit takes
  // 45-90 s; we cap visual progress at 95 % until the API actually returns).
  const [progressMs, setProgressMs] = useState(0);
  const { toggle, has } = useWishlist();
  // Per-mount seed → the Try-On picker shuffle is stable while the visitor
  // narrows by filters/search, but a fresh order every time they reopen
  // the picker. Differentiates the picker from the catalog (which always
  // sorts by created_at DESC and would otherwise show identical first rows).
  const pickerSeed = useMemo(() => Date.now() ^ Math.floor(Math.random() * 1e9), []);

  useEffect(() => {
    if (productId) {
      api.get(`/products/${productId}`).then((r) => setProduct(r.data)).catch(() => setError("Produto não encontrado"));
    } else {
      // Picker step: pull the full catalogue so the filter/search panel can
      // operate on everything (server only supports brand+category filters,
      // so the rest is client-side via applyCatalogFilters).
      //
      // Two adjustments vs the catalog so the picker doesn't show identical rows:
      //   1) Exclude "Outros" — those pieces short-circuit to the consultora
      //      flow (no real simulation), so showing them at the top of the
      //      picker only sets the customer up for frustration.
      //   2) Diversify the order (category + style + price band) with a
      //      mount-stable seed, so the first screen is visually varied.
      api.get("/products", { params: { limit: 5000 } }).then((r) => {
        const filtered = (r.data || []).filter((p) => {
          const c = (p.category || "").toLowerCase();
          return c !== "outros" && c !== "outro" && c !== "diversos";
        });
        const diversified = diversifyProducts(filtered, { seed: pickerSeed });
        setProducts(diversified);
      }).catch(() => {});
    }
  }, [productId, pickerSeed]);

  useEffect(() => {
    if (step !== "loading") return;
    const t = setInterval(() => setLoadingMsg((i) => (i + 1) % LOADING_MESSAGES.length), 2200);
    return () => clearInterval(t);
  }, [step]);

  // Drive the progress bar while a generation is in flight.
  useEffect(() => {
    if (step !== "loading") { setProgressMs(0); return; }
    setProgressMs(0);
    const start = Date.now();
    const t = setInterval(() => setProgressMs(Date.now() - start), 500);
    return () => clearInterval(t);
  }, [step]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBlob(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  // ----- Wait experience helpers -----
  // Capture the page title once at mount and ensure we always restore it when
  // the user returns to this tab (handles the case where the result lands
  // while the tab is hidden and we've changed the title to alert them).
  // Also closes the AudioContext on unmount so we don't leak audio sessions
  // across SPA navigations.
  useEffect(() => {
    if (!originalTitleRef.current) originalTitleRef.current = document.title;
    const handler = () => {
      if (!document.hidden && document.title !== originalTitleRef.current) {
        document.title = originalTitleRef.current;
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
      if (originalTitleRef.current) document.title = originalTitleRef.current;
      try {
        audioCtxRef.current?.close?.();
      } catch {/* ignore */}
      audioCtxRef.current = null;
    };
  }, []);

  // Soft 880 Hz pure-tone chime (~0.4s, gentle attack/decay) — plays when the
  // simulation finishes so a customer browsing in another tab gets an audible
  // cue. AudioContext must already be created/resumed via a user gesture for
  // this to actually be audible — we set that up in startAnalysis().
  const playChime = () => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    } catch {
      // ignore — chime is a nice-to-have, not critical
    }
  };

  // Fires after the analysis pipeline finishes. Plays the chime, hijacks
  // document.title if the tab is hidden, and shows a sonner toast. The toast
  // copy adapts to the actual outcome — "está pronta" for a real result,
  // "tem novidades" for controlled rejections (invalid_photo / timeout) so we
  // never tell the customer their simulation is ready when it isn't.
  const notifyComplete = (outcome = "success") => {
    playChime();
    const isSuccess = outcome === "success";
    const titleMsg = isSuccess ? "✨ Sua simulação está pronta!" : "✨ Sua simulação tem novidades";
    if (document.hidden && originalTitleRef.current) {
      document.title = titleMsg;
    }
    const toastFn = isSuccess ? toast.success : toast;
    toastFn(
      isSuccess
        ? "Sua simulação está pronta ✨"
        : "Sua simulação tem uma atualização ✨",
      { duration: 8000 },
    );
  };

  // Called from inside the wait-modal button onClick handlers. Critical: the
  // AudioContext is created/resumed *synchronously inside the click handler*
  // (this function), so the browser counts it as a user gesture and lets us
  // play sound later from an async callback.
  const startAnalysis = (openInNewTab) => {
    try {
      if (!audioCtxRef.current) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (Ctor) audioCtxRef.current = new Ctor();
      }
      if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
    } catch {
      // ignore — proceed without audio
    }
    setShowWaitModal(false);
    setStep("loading");
    if (openInNewTab) {
      // Open homepage in a new tab so the customer can keep browsing; the
      // original tab keeps polling in the background and will chime + toast
      // when the result is ready.
      window.open("/", "_blank", "noopener,noreferrer");
    }
    // Kick off the actual pipeline — analyze() now assumes step is already
    // "loading" and wraps its body in try/finally so notifyComplete fires
    // regardless of which terminal state we reach.
    analyze();
  };

  const analyze = async () => {
    if (!photoBlob || !product) return;
    setError("");
    // Step is already set to "loading" by startAnalysis() — don't reset it
    // here so the wait-modal → loading transition stays seamless.
    // `analysisStarted` flips true once the upload succeeds and we're truly
    // committed to running the analysis. We only fire notifyComplete() when
    // this flag is true so trivial upload-side errors don't trick the user
    // into thinking a result is ready.
    let analysisStarted = false;
    // Track the final outcome so notifyComplete can show contextual copy
    // ("está pronta" only when we actually have a result image, otherwise
    // "tem uma atualização").
    let outcome = "info";
    try {
      const fd = new FormData();
      fd.append("file", photoBlob, "photo.jpg");
      const up = await api.post("/uploads/public-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(up.data.path);
      analysisStarted = true;

      // Run visual try-on (OpenAI gpt-image-2) + GPT-4o consultation in parallel
      const visualPromise = api.post("/tryon/generate", { product_id: product.id, customer_photo_path: up.data.path })
        .then((r) => pollGenerate(r.data.job_id))
        .catch((e) => {
          // Limit reached returns 429 with structured detail — surface immediately.
          if (e?.response?.status === 429) {
            const d = e.response.data?.detail || {};
            return { image: null, limitReached: true, limit: d.limit, count: d.count, remainingDays: d.remaining_days };
          }
          console.warn("Visual try-on failed", e);
          return { image: null, error: "request" };
        });

      const gptPromise = api.post("/tryon/consultation", { product_id: product.id, customer_photo_path: up.data.path });

      const [visualRes, gpt] = await Promise.all([visualPromise, gptPromise]);
      if (visualRes && visualRes.limitReached) {
        // Backend rejected because monthly quota was already consumed.
        setQuota({ allowed: false, reason: "limit_reached", limit: visualRes.limit, count: visualRes.count, remaining_days: visualRes.remainingDays });
        setStep("limit_reached");
        return;
      }
      if (visualRes && visualRes.invalidReason) {
        // Photo does not show the required body region — show friendly screen, skip generation analysis
        setInvalidReason(visualRes.invalidReason);
        setResultImage(null);
        setResultError(null);
        setAnalysis(null);
        setStep("invalid_photo");
        return;
      }
      if (visualRes && visualRes.invalidGenerated) {
        // Layer 3 (post-generation QC) rejected the result — reuse the
        // invalid_photo flow visually so the customer sees the same elegant
        // "tente outra foto" screen with the "Tentar outra vez" button.
        // Quota is NOT debited (handled backend-side).
        setInvalidReason(visualRes.invalidGenerated);
        setResultImage(null);
        setResultError(null);
        setAnalysis(null);
        setStep("invalid_photo");
        return;
      }
      if (visualRes && visualRes.consultoraRequired) {
        // categoria "Outros" — não simula, sem cota debitada — direciona pra
        // consultora com mensagem elegante.
        setResultImage(null);
        setResultError(null);
        setAnalysis(null);
        setStep("consultora_required");
        return;
      }
      if (visualRes && visualRes.timedOut) {
        // Polite retry screen — quota was NOT consumed.
        setResultImage(null);
        setResultError(null);
        setAnalysis(null);
        setStep("timeout");
        return;
      }
      if (visualRes && visualRes.image) {
        setResultImage(visualRes.image);
        setResultError(null);
        outcome = "success";
      } else if (visualRes && visualRes.error) {
        setResultImage(null);
        setResultError(visualRes.error);
      }
      setAnalysis(gpt.data.analysis);
      setComplement(gpt.data.complement);
      api.post("/metrics/event", { event_type: "tryon", product_id: product.id }).catch(() => {});
      setStep("result");
    } catch (e) {
      setError("Não foi possível analisar a foto. " + (e?.response?.data?.detail || ""));
      setStep("photo");
    } finally {
      if (analysisStarted) notifyComplete(outcome);
    }
  };

  // Poll the generation job for up to ~7 minutes (matches backend
  // TRYON_TIMEOUT_SECONDS=420). Backend marks status="timeout" automatically
  // once the window elapses; we also self-time out at the same boundary so
  // the customer never sees a perpetually spinning loader.
  // 210 attempts × 2s = 420s = 7 min.
  const pollGenerate = async (jobId, maxAttempts = 210) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const s = await api.get(`/tryon/generate/${jobId}`);
        if (s.data.status === "completed") {
          const raw = s.data.result_image_url;
          if (!raw) return { image: null, error: "empty" };
          const url = raw.startsWith("http") ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
          return { image: url, error: null };
        }
        if (s.data.status === "invalid_photo") {
          return { image: null, invalidReason: s.data.reason || "Foto não atende ao requisito desta categoria." };
        }
        if (s.data.status === "invalid_generation") {
          // Layer 3 reprovou — não debita cota, retorna ao fluxo "invalid"
          return { image: null, invalidGenerated: s.data.reason || "A simulação não passou na verificação de qualidade." };
        }
        if (s.data.status === "consultora_required") {
          // Categoria "Outros" — não gerou imagem, redireciona para a consultora.
          return { image: null, consultoraRequired: true };
        }
        if (s.data.status === "timeout") {
          return { image: null, timedOut: true };
        }
        if (s.data.status === "failed") {
          return { image: null, error: s.data.error || "failed" };
        }
      } catch { return { image: null, error: "polling" }; }
    }
    return { image: null, timedOut: true };
  };

  const reset = () => {
    setPhotoBlob(null);
    setPhotoPreview(null);
    setPhotoPath(null);
    setAnalysis(null);
    setComplement(null);
    setResultImage(null);
    setResultError(null);
    setInvalidReason(null);
    setStep("photo");
  };

  // ----- gate: still resolving auth / quota -----
  if (authLoading || !user || quotaLoading) {
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-gate-loading">
        <div className="w-3 h-3 bg-[#B8A88A] rounded-full pulse-accent mb-6" />
        <p className="font-italic-display text-lg text-[#6B6B6B]">Preparando sua experiência...</p>
      </div>
    );
  }

  // ----- gate: monthly limit reached -----
  if ((quota && quota.allowed === false) || step === "limit_reached") {
    const limit = quota?.limit ?? 30;
    const count = quota?.count ?? limit;
    const days = quota?.remaining_days ?? null;
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-limit-reached">
        <div className="max-w-md w-full bg-white border border-[#E8E4DF] p-10 text-center">
          <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
          <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center rounded-full bg-[#F5EFE8] text-[#B8A88A]">
            <Clock size={26} strokeWidth={1.4}/>
          </div>
          <h2 className="font-display text-3xl md:text-4xl mb-4">Você usou todas as suas simulações deste mês</h2>
          <p className="text-[15px] text-[#4A4A4A] leading-relaxed mb-2" data-testid="limit-summary">
            Realizou <strong>{count}</strong> de <strong>{limit}</strong> simulações disponíveis em {new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}.
          </p>
          {days != null && (
            <p className="text-sm text-[#6B6B6B] mb-8" data-testid="limit-renew">
              Seu limite renova em <strong>{days} dia{days === 1 ? "" : "s"}</strong>.
            </p>
          )}
          <p className="font-italic-display text-base text-[#8C7B5E] mb-8 leading-relaxed">
            Enquanto isso, que tal explorar nossa curadoria ou salvar suas peças favoritas?
          </p>
          <div className="space-y-3">
            <Link to="/novidades" className="btn-primary w-full" data-testid="limit-browse-btn">
              Explorar catálogo
            </Link>
            <Link to="/wishlist" className="btn-secondary w-full" data-testid="limit-wishlist-btn">
              Ver minha wishlist
            </Link>
          </div>

          {/* Discreet fallback CTA — for customers who exhausted their monthly
              quota and want a human-curated alternative. Reuses the shared
              ConsultoraModal so the experience matches the rest of the site
              (home, Try-On result, invalid_photo). Visually secondary so it
              does not compete with the "Explorar catálogo" primary CTA. */}
          <div className="mt-8 pt-6 border-t border-[#E8E4DF] text-center">
            <p className="text-sm text-[#8C7B5E] italic leading-relaxed mb-4">
              Quer mais simulações ou prefere uma experiência ainda mais personalizada?
              <br/>Nossas consultoras estão prontas para te atender.
            </p>
            <button
              type="button"
              onClick={() => setConsultoraOpen(true)}
              className="inline-block w-full border border-[#B8A88A] text-[#B8A88A] hover:bg-[#F5EFE8] hover:text-[#8C7B5E] py-3 text-xs uppercase tracking-[0.15em] transition-colors"
              style={{ fontFamily: "'Jost', sans-serif" }}
              data-testid="limit-consultora-cta"
            >
              Falar com uma consultora →
            </button>
          </div>
        </div>
        <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
      </div>
    );
  }

  // ----- choose-product step -----
  if (step === "choose-product") {
    // Apply server-already-fetched products through the same filter pipeline
    // as the main catalogue (brand + category + style + occasion + forWhom +
    // combines + sophistication + price range) plus a free-text name search.
    // We treat `pickerFilters.brand` and `pickerFilters.category` as additional
    // client-side facets here since we fetched everything in one go.
    const q = pickerSearch.trim().toLowerCase();
    let list = products;
    if (pickerFilters.brand) list = list.filter((p) => (displayBrand(p) || "").toLowerCase() === pickerFilters.brand.toLowerCase());
    if (pickerFilters.category) list = list.filter((p) => p.category === pickerFilters.category);
    list = applyCatalogFilters(list, pickerFilters);
    if (q) list = list.filter((p) => `${p.name || ""} ${displayBrand(p) || ""}`.toLowerCase().includes(q));

    const clearPicker = () => { setPickerFilters({ ...EMPTY_FILTERS }); setPickerSearch(""); };

    return (
      <div className="bg-[#FAF9F7] min-h-screen py-16" data-testid="tryon-choose-product">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/try-on" className="overline text-[#6B6B6B] hover:text-[#B8A88A] inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={14}/> Voltar
          </Link>
          <h1 className="font-display text-4xl md:text-5xl mb-4">Escolha uma peça</h1>
          <p className="text-[#6B6B6B] mb-8">Selecione qualquer joia do catálogo para experimentar.</p>

          {/* Search bar + mobile filters trigger */}
          <div className="flex items-center gap-3 mb-8">
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8A8]" />
              <input
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Buscar por nome ou marca..."
                className="w-full border border-[#E8E4DF] pl-10 pr-3 py-2.5 text-sm bg-white focus:border-[#B8A88A] outline-none"
                data-testid="picker-search-input"
              />
            </div>
            <button
              data-testid="picker-open-mobile-filters"
              className="md:hidden inline-flex items-center gap-2 overline border border-[#E8E4DF] px-3 py-2.5"
              onClick={() => setPickerMobileFiltersOpen(true)}
            >
              <SlidersHorizontal size={14} /> Filtros
            </button>
          </div>

          <div className="grid md:grid-cols-[240px_1fr] gap-10">
            <aside className="hidden md:block">
              <CatalogFilterPanel filters={pickerFilters} setFilters={setPickerFilters} showCategory={true} onClear={clearPicker} />
            </aside>
            <div>
              <p className="text-sm text-[#6B6B6B] mb-6" data-testid="picker-results-count">{list.length} {list.length === 1 ? "peça" : "peças"}</p>
              {list.length === 0 ? (
                <div className="text-center py-20">
                  <p className="font-display text-2xl mb-2">Nenhuma peça encontrada</p>
                  <p className="text-[#6B6B6B] text-sm">Tente ajustar os filtros ou a busca.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  {list.map((p) => {
                    const img = p.images?.[0] ? FILE_URL(p.images[0]) : "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=500&q=80";
                    return (
                      <button
                        key={p.id}
                        data-testid={`choose-product-${p.id}`}
                        onClick={() => { setProduct(p); setStep("photo"); }}
                        className="text-left group"
                      >
                        <div className="aspect-[3/4] overflow-hidden bg-white">
                          <img src={img} alt={p.name} className="w-full h-full object-cover img-hover" />
                        </div>
                        {(() => { const b = displayBrand(p); return b ? <p className="text-[10px] uppercase tracking-wider text-[#A8A8A8] mt-3">{b}</p> : null; })()}
                        <p className="font-display text-base mt-1">{p.name}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile filters bottom sheet — same pattern as CategoryPage */}
        {pickerMobileFiltersOpen && (
          <div className="fixed inset-0 bg-black/40 z-[100] md:hidden flex items-end" onClick={() => setPickerMobileFiltersOpen(false)}>
            <div className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-xl p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl">Filtros</h3>
                <button onClick={() => setPickerMobileFiltersOpen(false)} data-testid="picker-close-mobile-filters">
                  <X size={20} />
                </button>
              </div>
              <CatalogFilterPanel filters={pickerFilters} setFilters={setPickerFilters} showCategory={true} onClear={clearPicker} />
              <button className="btn-primary w-full mt-8" onClick={() => setPickerMobileFiltersOpen(false)}>
                Aplicar filtros
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ----- photo step -----
  if (step === "photo") {
    if (!product) return <div className="p-20 text-center text-[#A8A8A8]">Carregando peça...</div>;
    const productImg = product.images?.[0] ? FILE_URL(product.images[0]) : "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=500&q=80";
    const instructionByCategory = (cat) => {
      const c = (cat || "").toLowerCase();
      if (c.includes("colar")) return {
        key: "colar",
        text: "Para o melhor resultado, envie uma foto com o pescoço aparecendo — de frente ou levemente de lado, com o colo à mostra.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4c-3 0-5 2-5 5v3"/>
            <path d="M21 12V9c0-3-2-5-5-5"/>
            <path d="M8 14c0 5 4 7 8 7s8-2 8-7"/>
            <circle cx="16" cy="22" r="1.5"/>
          </svg>
        ),
      };
      if (c.includes("anel")) return {
        key: "anel",
        text: "Envie uma foto com pelo menos uma mão visível, dedos abertos e estendidos sobre uma superfície clara.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 14V8a2 2 0 0 1 4 0v6"/>
            <path d="M15 14V6a2 2 0 0 1 4 0v8"/>
            <path d="M19 14V8a2 2 0 0 1 4 0v10c0 4-3 8-7 8s-7-3-7-7"/>
            <path d="M11 14V10a2 2 0 0 0-4 0v8"/>
          </svg>
        ),
      };
      if (c.includes("brinco")) return {
        key: "brinco",
        text: "Envie uma foto com pelo menos uma orelha aparecendo — de frente ou levemente de lado, com o cabelo preso ou para o lado.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 6c-4 0-7 3-7 8 0 4 2 7 5 8 1 0 2 1 2 2v2"/>
            <path d="M14 12c0-2 1-3 3-3s3 1 3 3-2 3-3 4"/>
            <circle cx="17" cy="18" r="1.2"/>
          </svg>
        ),
      };
      if (c.includes("pingente")) return {
        key: "pingente",
        text: "Envie uma foto com o pescoço aparecendo — de frente ou levemente de lado, com o colo à mostra.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 14c0 5 4 7 8 7s8-2 8-7"/>
            <path d="M16 21v3"/>
            <circle cx="16" cy="26" r="2"/>
          </svg>
        ),
      };
      if (c.includes("pulseira")) return {
        key: "pulseira",
        text: "Envie uma foto com o punho visível — mão relaxada, manga para cima.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="16" cy="16" rx="9" ry="4"/>
            <path d="M7 16c0 2 4 4 9 4"/>
          </svg>
        ),
      };
      return {
        key: "outro",
        text: "Envie uma foto do corpo inteiro ou da região onde a peça seria usada, com boa iluminação.",
        icon: (
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="16" cy="8" r="3"/>
            <path d="M9 27v-6a7 7 0 0 1 14 0v6"/>
          </svg>
        ),
      };
    };
    const inst = instructionByCategory(product.category);
    return (
      <div className="bg-[#FAF9F7] min-h-screen py-16" data-testid="tryon-photo-step">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/try-on" className="overline text-[#6B6B6B] hover:text-[#B8A88A] inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={14}/> Voltar
          </Link>

          {/* Product confirm */}
          <div className="bg-white p-6 md:p-8 mb-10 flex items-center gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 bg-[#FAF9F7] overflow-hidden flex-shrink-0">
              <img src={productImg} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              {(() => { const b = displayBrand(product); return b ? <p className="overline text-[#A8A8A8] mb-1">{b}</p> : null; })()}
              <h2 className="font-display text-2xl md:text-3xl mb-1">{product.name}</h2>
              <p className="text-sm text-[#6B6B6B]">{formatBRL(product.price)}</p>
              <button
                data-testid="change-product-btn"
                onClick={() => { setProduct(null); setStep("choose-product"); }}
                className="text-xs text-[#B8A88A] hover:text-[#8C7B5E] mt-2 underline"
              >
                Trocar peça
              </button>
            </div>
          </div>

          <p className="font-italic-display text-xl text-[#6B6B6B] mb-10 text-center">
            Ótima escolha. Agora vamos ver como essa peça fica em você.
          </p>

          {!photoPreview ? (
            <>
              {/* Per-category photo instruction — sutil, fundo #FAF9F7, borda #E8E4DF, Jost 13px #6B6B6B */}
              <div
                className="mb-6 border border-[#E8E4DF] bg-[#FAF9F7] p-5 flex items-start gap-4"
                data-testid={`photo-instruction-${inst.key}`}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-[#B8A88A]">
                  {inst.icon}
                </div>
                <p className="text-[13px] text-[#6B6B6B] leading-relaxed pt-1" style={{ fontFamily: "'Jost', sans-serif" }}>
                  {inst.text}
                </p>
              </div>

              {/* Upload — single, full-width option (camera capture removed) */}
              <label
                className="bg-white p-10 border border-[#E8E4DF] hover:border-[#B8A88A] transition-colors text-center cursor-pointer block"
                data-testid="upload-btn"
              >
                <Upload size={32} className="text-[#B8A88A] mb-4 mx-auto" />
                <h3 className="font-display text-xl mb-1">Enviar da galeria</h3>
                <p className="text-sm text-[#6B6B6B]">Faça upload de uma foto</p>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={onFileChange}
                  className="hidden"
                />
              </label>
              {/* Privacy notice — discreet reassurance about photo handling.
                  Sized to be clearly legible (text-sm) while still stepping
                  down from the upload title above; icon scaled proportionally. */}
              <p
                className="mt-3 text-sm text-[#6B6B6B] flex items-center justify-center gap-2 leading-relaxed"
                data-testid="tryon-privacy-notice"
              >
                <ShieldCheck size={16} className="flex-shrink-0" aria-hidden />
                <span>
                  Sua foto será usada exclusivamente para criar sua simulação e apagada
                  imediatamente após você fechar o site.
                </span>
              </p>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="mt-8 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] flex items-center gap-2 mx-auto"
                data-testid="show-tips-btn"
              >
                <Info size={16} /> Dicas para uma boa foto
              </button>
              {showInfo && (
                <div className="mt-4 text-sm text-[#6B6B6B] bg-white p-5 border border-[#E8E4DF]">
                  <ul className="space-y-1.5 list-disc pl-4">
                    <li>Boa iluminação natural, sem flash forte</li>
                    <li>Fundo neutro</li>
                    <li>Foto nítida, sem filtros</li>
                    <li>Posição natural, sem maquiagem pesada se possível</li>
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="max-w-md mx-auto aspect-[3/4] overflow-hidden mb-6 bg-white">
                <img src={photoPreview} alt="Sua foto" className="w-full h-full object-contain" data-testid="photo-preview" />
              </div>
              <div className="flex items-center justify-center gap-3 mb-2">
                <button
                  data-testid="change-photo-btn"
                  onClick={() => { setPhotoBlob(null); setPhotoPreview(null); }}
                  className="btn-secondary"
                >
                  <RefreshCw size={14} className="mr-2" /> Trocar foto
                </button>
                <button data-testid="analyze-btn" onClick={() => setShowWaitModal(true)} className="btn-accent">
                  <Sparkles size={14} className="mr-2" /> Analisar agora
                </button>
              </div>
            </div>
          )}

          {error && <p data-testid="error-msg" className="text-sm text-red-600 mt-6 text-center">{error}</p>}
        </div>

        {/* Pre-generation wait experience modal — gives the customer a clear
            choice between staying on this tab (existing progress UI) or
            continuing to browse the catalog while we keep polling in the
            background and chime + toast + retitle when ready. */}
        {showWaitModal && (
          <div
            data-testid="tryon-wait-modal"
            className="fixed inset-0 z-[200] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6"
          >
            <div className="bg-white border border-[#E8E4DF] w-full md:max-w-md md:rounded-none rounded-t-2xl p-8 md:p-10 text-center">
              <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
              <h3 className="font-display text-2xl md:text-3xl mb-4" data-testid="wait-modal-title">
                Sua simulação está sendo criada ✨
              </h3>
              <p className="text-[14px] text-[#4A4A4A] leading-relaxed mb-3">
                Esse processo pode levar alguns minutos. Você pode aguardar aqui ou continuar explorando o catálogo — avisaremos quando estiver pronta.
              </p>
              <p className="font-italic-display text-[13px] text-[#8C7B5E] mb-8">
                Não feche esta aba para receber o resultado.
              </p>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => startAnalysis(true)}
                  className="btn-primary w-full"
                  data-testid="wait-continue-explore-btn"
                >
                  Continuar explorando
                </button>
                <button
                  type="button"
                  onClick={() => startAnalysis(false)}
                  className="btn-secondary w-full"
                  data-testid="wait-stay-here-btn"
                >
                  Prefiro aguardar aqui
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ----- loading -----
  if (step === "loading") {
    // Estimated total duration for gpt-image-2 high-quality edit (it engages the
    // "thinking" pipeline at quality=high, so latency is naturally higher than
    // gpt-image-1.5). We cap visual progress at 95 % until the API actually
    // returns — the last 5 % fills in instantly on success.
    const ESTIMATED_TOTAL_MS = 90_000;
    const rawPct = Math.min(95, Math.round((progressMs / ESTIMATED_TOTAL_MS) * 100));
    const remainingSec = Math.max(0, Math.ceil((ESTIMATED_TOTAL_MS - progressMs) / 1000));
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-loading">
        <div className="w-3 h-3 bg-[#B8A88A] rounded-full pulse-accent mb-8" />
        <p className="font-italic-display text-2xl md:text-3xl text-[#6B6B6B] text-center max-w-md mb-10">
          {LOADING_MESSAGES[loadingMsg]}
        </p>
        <div className="w-full max-w-sm" data-testid="tryon-progress">
          <div className="h-[3px] bg-[#E8E4DF] overflow-hidden">
            <div
              className="h-full bg-[#B8A88A] transition-all duration-500 ease-linear"
              style={{ width: `${rawPct}%` }}
              data-testid="tryon-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-3 text-[11px] tracking-wider text-[#A8A8A8] uppercase">
            <span data-testid="tryon-progress-pct">{rawPct}%</span>
            <span data-testid="tryon-progress-eta">
              {remainingSec > 0 ? `~${remainingSec}s restantes` : "Finalizando..."}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ----- timed-out: gentle retry screen (does NOT consume the user's quota) -----
  if (step === "timeout") {
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-timeout">
        <div className="max-w-md w-full bg-white border border-[#E8E4DF] p-10 text-center">
          <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
          <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center rounded-full bg-[#F5EFE8] text-[#B8A88A]">
            <Clock size={26} strokeWidth={1.4}/>
          </div>
          <h2 className="font-display text-3xl md:text-4xl mb-4">Quase lá</h2>
          <p className="text-[15px] text-[#4A4A4A] leading-relaxed mb-6">
            A simulação demorou um pouco mais que o esperado. Vamos tentar de novo? Verifique sua conexão e clique abaixo.
          </p>
          <p className="font-italic-display text-sm text-[#8C7B5E] mb-8">
            Sua simulação deste mês ainda não foi consumida.
          </p>
          <button onClick={reset} className="btn-primary w-full" data-testid="tryon-retry-btn">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ----- categoria "Outros" — não há simulação visual, vai direto para consultora -----
  if (step === "consultora_required") {
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-consultora-required">
        <div className="max-w-md w-full bg-white border border-[#E8E4DF] p-10 text-center">
          <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
          <h2 className="font-display text-3xl md:text-4xl mb-6">Uma peça especial</h2>
          <p className="text-[15px] text-[#4A4A4A] leading-relaxed mb-8">
            Esta joia é uma peça especial que merece uma experiência ainda mais personalizada.
            A simulação virtual não está disponível para ela, mas nossas consultoras adorariam te ajudar a visualizá-la. Fale com a gente!
          </p>
          <button
            onClick={() => setConsultoraOpen(true)}
            className="btn-primary w-full"
            data-testid="consultora-required-btn"
          >
            Falar com uma consultora
          </button>
        </div>
        <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
      </div>
    );
  }

  // ----- photo did not pass validation (e.g. selfie without neck for a necklace) -----
  if (step === "invalid_photo") {
    // Category-specific elegant message. Tone: warm + encouraging, never
    // technical. The customer's quota is NOT debited for this case (handled
    // backend-side: job ends with status="invalid_photo" before generation).
    const cat = (product?.category || "").toLowerCase();
    const messageByCategory = {
      "brincos": "Para experimentar este brinco, precisamos de uma foto que mostre ao menos uma das suas orelhas. Que tal tirar uma selfie de lado ou de frente?",
      "colares": "Para experimentar este colar, precisamos de uma foto que mostre seu pescoço. Uma foto do busto para cima funciona perfeitamente.",
      "pingentes": "Para experimentar este colar, precisamos de uma foto que mostre seu pescoço. Uma foto do busto para cima funciona perfeitamente.",
      "anéis": "Para experimentar este anel, precisamos de uma foto que mostre sua mão com os dedos visíveis.",
      "aneis": "Para experimentar este anel, precisamos de uma foto que mostre sua mão com os dedos visíveis.",
      "pulseiras": "Para experimentar esta pulseira, precisamos de uma foto que mostre seu pulso.",
    };
    const friendlyMsg = messageByCategory[cat] || invalidReason || "Para esta peça, precisamos de uma foto que mostre a região do corpo onde ela será usada.";
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="tryon-invalid-photo">
        <div className="max-w-md w-full bg-white border border-[#E8E4DF] p-10 text-center">
          <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
          <h2 className="font-display text-3xl md:text-4xl mb-6">Vamos tentar outra foto?</h2>
          <p className="text-[15px] text-[#4A4A4A] leading-relaxed mb-8" data-testid="invalid-reason">
            {friendlyMsg}
          </p>
          <button onClick={reset} className="btn-primary w-full" data-testid="try-another-photo-btn">
            Tentar outra vez
          </button>

          {/* Discreet fallback — if photo validation keeps failing, offer
              human consultation via the shared ConsultoraModal. Visually
              secondary to "Tentar outra vez" so the primary action stays
              dominant. */}
          <div className="mt-6 pt-5 border-t border-[#E8E4DF]">
            <p className="text-xs text-[#8C7B5E] italic mb-3 leading-relaxed">
              Está com dificuldades ou prefere uma experiência personalizada?
            </p>
            <button
              type="button"
              onClick={() => setConsultoraOpen(true)}
              className="overline text-[#B8A88A] hover:text-[#8C7B5E] underline underline-offset-4"
              data-testid="invalid-photo-consultora-cta"
            >
              Falar com uma consultora →
            </button>
          </div>
        </div>
        <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
      </div>
    );
  }

  // ----- result -----
  if (step === "result" && analysis) {
    const isFav = product && has(product.id);
    return (
      <div className="bg-white min-h-screen py-12" data-testid="tryon-result">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            <div className="relative">
              <div className="aspect-[3/4] bg-[#FAF9F7] overflow-hidden">
                <img src={resultImage || photoPreview} alt="Você" className="w-full h-full object-contain" data-testid="result-photo"/>
              </div>
              {resultImage && (
                <div className="absolute top-3 left-3 bg-[#B8A88A] text-white text-[10px] uppercase tracking-wider px-2.5 py-1 inline-flex items-center gap-1">
                  <Sparkles size={10}/> Simulação visual
                </div>
              )}
              {!resultImage && resultError && (
                <div className="absolute top-3 left-3 right-3 bg-[#FFF8EE] border border-[#E8C994] text-[#7A5A1A] text-[11px] px-3 py-2" data-testid="tryon-error-notice">
                  Não foi possível gerar a simulação visual desta vez{resultError === "billing_hard_limit_reached" || /billing|limit|quota/i.test(String(resultError)) ? " — limite da OpenAI atingido. Adicione saldo à sua conta OpenAI." : ". Tente novamente em instantes."}
                </div>
              )}
              <div className="absolute bottom-6 left-6 right-6 bg-white p-4 border-l-2 border-[#B8A88A]" data-testid="compat-card">
                <p className="overline text-[#B8A88A] mb-1">✦ {product.name}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#6B6B6B]">Compatibilidade</span>
                  <span className="font-display text-lg" data-testid="compat-label">{compatLabel(analysis.compatibilidade)}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="overline text-[#B8A88A] mb-5">✦ Sua análise</p>
              <p className="font-display text-2xl md:text-3xl leading-snug mb-8" data-testid="analysis-comment">
                {analysis.comentario_principal}
              </p>
              <div className="border-t border-[#E8E4DF] pt-6 mb-6">
                <p className="overline text-[#A8A8A8] mb-2">O que se destaca em você</p>
                <p className="text-[#1A1A1A]" data-testid="analysis-destaque">{analysis.destaque_visual}</p>
              </div>
              <div className="border-t border-[#E8E4DF] pt-6 mb-10">
                <p className="overline text-[#A8A8A8] mb-2">Como usar</p>
                <p className="text-[#1A1A1A]" data-testid="analysis-dica">{analysis.dica_uso}</p>
              </div>

              {complement && (
                <div className="bg-[#FAF9F7] p-6 mb-8 flex items-center gap-5" data-testid="complement-card">
                  <div className="w-20 h-20 bg-white overflow-hidden flex-shrink-0">
                    <img
                      src={complement.images?.[0] ? FILE_URL(complement.images[0]) : "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=300&q=80"}
                      alt={complement.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-italic-display text-[#8C7B5E] mb-1">
                      {analysis.sugestao_complemento || "Para completar o look:"}
                    </p>
                    <p className="font-display text-lg">{complement.name}</p>
                    <p className="text-xs text-[#6B6B6B]">{formatBRL(complement.price)}</p>
                  </div>
                  <Link to={`/produto/${complement.id}`} className="overline text-[#B8A88A] hover:text-[#8C7B5E]">
                    Ver →
                  </Link>
                </div>
              )}

              <div className="space-y-3">
                <button onClick={() => nav(`/redirect?produto=${product.id}`)} className="btn-primary w-full" data-testid="result-buy-btn">
                  Comprar esta peça
                </button>
                {resultImage && (
                  <button
                    type="button"
                    data-testid="open-share-btn"
                    onClick={() => setShareOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[#B8A88A] text-[#B8A88A] hover:bg-[#F5EFE8] transition-colors text-sm tracking-wider uppercase"
                  >
                    Compartilhar <Share2 size={14}/>
                  </button>
                )}
                <button
                  onClick={() => toggle(product)}
                  className="w-full text-sm py-3 text-[#6B6B6B] hover:text-[#B8A88A] inline-flex items-center justify-center gap-2"
                  data-testid="result-wishlist-btn"
                >
                  <Heart size={14} fill={isFav ? "#B8A88A" : "none"}/>
                  {isFav ? "Salvo na wishlist" : "Salvar na wishlist"}
                </button>

                {/* Discreet escape hatch — caso a simulação não tenha convencido a cliente, oferecemos
                    consultoria humana sem fricção. Usa o mesmo modal de consultora do resto do site. */}
                <div className="mt-6 pt-5 border-t border-[#E8E4DF] text-center">
                  <p className="text-sm text-[#8C7B5E] italic mb-2 leading-relaxed">
                    Sua simulação não deu certo ou não gostou do resultado?
                    <br/>Marque uma consultoria digital e fazemos ela para você!
                  </p>
                  <button
                    type="button"
                    onClick={() => setConsultoraOpen(true)}
                    className="overline text-[#B8A88A] hover:text-[#8C7B5E] underline underline-offset-4"
                    data-testid="result-consultora-cta"
                  >
                    Falar com uma consultora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shared consultora modal for the CTA above */}
        <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />

        {/* Share modal — desktop center, mobile bottom sheet */}
        {shareOpen && resultImage && (
          <div
            data-testid="share-modal"
            className="fixed inset-0 z-[200] bg-black/40 flex items-end md:items-center justify-center"
            onClick={() => setShareOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white border-2 border-[#B8A88A] w-full md:max-w-md md:rounded-none rounded-t-2xl md:rounded-t-none p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl">Compartilhar</h3>
                <button onClick={() => setShareOpen(false)} data-testid="share-close-btn" className="text-[#6B6B6B] hover:text-[#1A1A1A]">
                  <X size={18}/>
                </button>
              </div>
              <p className="text-sm text-[#6B6B6B] mb-6">Escolha como deseja compartilhar a simulação:</p>
              <div className={`grid gap-3 ${IS_MOBILE ? "grid-cols-3" : "grid-cols-2"}`}>
                <button
                  data-testid="share-whatsapp-btn"
                  onClick={async () => {
                    const text = `Olha como ficou! Estou experimentando ${product.name} da Prize Joias ✨ ${window.location.origin}/produto/${product.id}`;
                    // Try Web Share API with file first (sends the image inline
                    // on iOS/Android WhatsApp). Fall back to wa.me link-only on
                    // desktop or when the OS refuses the file share.
                    const ok = await nativeShareWithFile({
                      imageUrl: resultImage,
                      filename: `prize-joias-${product.code || "tryon"}.jpg`,
                      text,
                    });
                    if (!ok) {
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(text)}`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                    setShareOpen(false);
                  }}
                  className="flex flex-col items-center justify-center gap-3 py-6 bg-[#FAF9F7] hover:bg-[#F5EFE8] transition-colors"
                >
                  <MessageCircle size={22} className="text-[#B8A88A]"/>
                  <span className="text-sm font-medium">WhatsApp</span>
                </button>
                {IS_MOBILE && (
                  <button
                    data-testid="share-instagram-btn"
                    onClick={async () => {
                      // Caption that the user will paste on the Story sticker
                      // (Instagram does not accept text alongside shared files,
                      // so we copy it to the clipboard for one-tap paste).
                      const caption = "Olha como ficou ✨ Experimentei essa joia da @prizejoias sem sair de casa";
                      try {
                        await navigator.clipboard?.writeText(caption);
                        toast.success("Legenda copiada — cole no sticker do Story");
                      } catch {/* ignore — share still works */}
                      const ok = await nativeShareWithFile({
                        imageUrl: resultImage,
                        filename: `prize-joias-story-${product.code || "tryon"}.jpg`,
                        text: caption,
                      });
                      if (!ok) {
                        toast.error("Seu navegador móvel não suporta compartilhamento direto. Salve a imagem e poste manualmente.");
                      }
                      setShareOpen(false);
                    }}
                    className="flex flex-col items-center justify-center gap-3 py-6 bg-[#FAF9F7] hover:bg-[#F5EFE8] transition-colors group"
                  >
                    <Instagram size={22} className="text-[#E1306C] group-hover:text-[#C13584] transition-colors"/>
                    <span className="text-sm font-medium">Instagram</span>
                  </button>
                )}
                <button
                  data-testid="download-image-btn"
                  onClick={async () => {
                    try {
                      const r = await fetch(resultImage);
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `prize-joias-${product.code || "tryon"}.jpg`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success("Imagem salva");
                    } catch {
                      toast.error("Não foi possível baixar");
                    }
                    setShareOpen(false);
                  }}
                  className="flex flex-col items-center justify-center gap-3 py-6 bg-[#FAF9F7] hover:bg-[#F5EFE8] transition-colors"
                >
                  <Download size={22} className="text-[#B8A88A]"/>
                  <span className="text-sm font-medium">Baixar imagem</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
